import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, IconButton, Fade, TooltipProps, Box } from '@mui/material';
import { ContentCopy as ContentCopyIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

import { TypographyGrey } from '/styles/shared/styledText';
import { useModal } from '/hooks';

import { CustomPopover, SpanCursor, resetDefaultSetting } from './styles';
import { MAX_TEXT_WIDTH, TOOLTIP_CLOSE_DELAY } from './constant';
import { HiddenText } from './HiddenText';

interface ITooltipCellProps extends Omit<TooltipProps, 'title' | 'children'> {
  item: string;
  label?: string;
  customText?: string;
  children?: ReactNode;
}

const CustomTooltipContent = ({
  label = 'Entity',
  item,
  close,
  customText,
  isOpen,
}: {
  item: string;
  label?: string;
  customText?: string;
  close: VoidFunction;
  isOpen: boolean;
}) => {
  const [copied, setCopied] = useState(false);
  const [fixedWidth, setFixedWidth] = useState(0);

  const initialTextRef = useRef<HTMLElement | null>(null);
  const copiedTextRef = useRef<HTMLElement | null>(null);

  const initialText = customText ?? item;
  const copiedText = `${label} Copied`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(item);
    setCopied(true);
    setTimeout(() => close(), TOOLTIP_CLOSE_DELAY);
  };

  useLayoutEffect(() => {
    if (isOpen && fixedWidth === 0) {
      const initialWidth = initialTextRef.current?.offsetWidth ?? 0;
      const copiedWidth = copiedTextRef.current?.offsetWidth ?? 0;

      if (initialWidth > 0 || copiedWidth > 0) {
        setFixedWidth(Math.max(initialWidth, copiedWidth));
      }
    }
  }, [isOpen, fixedWidth, initialText, copiedText]);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    const resetTimer = setTimeout(() => {
      setCopied(false);
      setFixedWidth(0);
    }, TOOLTIP_CLOSE_DELAY);

    return () => clearTimeout(resetTimer);
  }, [isOpen]);

  return (
    <>
      {isOpen && fixedWidth === 0 && (
        <HiddenText
          initialTextRef={initialTextRef}
          copiedTextRef={copiedTextRef}
          initialText={initialText}
          copiedText={copiedText}
        />
      )}

      <CustomPopover boxShadow={3}>
        <TypographyGrey
          variant='body2'
          minWidth={fixedWidth > 0 ? `${fixedWidth}px` : 'auto'}
          maxWidth={MAX_TEXT_WIDTH}
          fontWeight={500}
          sx={{
            wordBreak: 'break-all',
          }}
        >
          {copied ? copiedText : initialText}
        </TypographyGrey>

        {copied ? (
          <Box p='5px' display='flex' alignItems='center'>
            <CheckCircleIcon color='success' fontSize='small' />
          </Box>
        ) : (
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
          >
            <ContentCopyIcon fontSize='small' />
          </IconButton>
        )}
      </CustomPopover>
    </>
  );
};

export const TooltipWithPopover = ({
  label,
  customText,
  children,
  item,
  placement = 'top-start',
  ...styles
}: ITooltipCellProps) => {
  const { open, close, isOpen } = useModal();

  return (
    <Tooltip
      {...styles}
      open={isOpen}
      onClose={close}
      onOpen={open}
      placement={placement}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: TOOLTIP_CLOSE_DELAY }}
      title={<CustomTooltipContent label={label} customText={customText} item={item} close={close} isOpen={isOpen} />}
      componentsProps={{
        tooltip: {
          sx: resetDefaultSetting,
        },
      }}
    >
      <SpanCursor disableHoverListener={styles.disableHoverListener}>{children ?? item}</SpanCursor>
    </Tooltip>
  );
};
